import { useLazyQuery } from '@apollo/client';
import {
  HostStatementDocument,
  HostStatementQuery,
  Currency,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import Papa from 'papaparse';
import PriceFormat from '@website/components/utilities/PriceFormat';
import createMonthsList from './utils/createMonthsList';

export function formatCSV(data: HostStatementQuery) {
  // Remove zero lines
  const lines = data.hostByURL.statement.lines.filter(
    (line) => line.hostAmount.amountZD !== 0,
  );

  return lines.map((line) => {
    const trip = data.hostByURL.trips.items.find((i) => i.id === line.tripID)!;

    const hostAmountRounded = line.hostAmount.amountZD / 100;
    const hostAmount = hostAmountRounded.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });

    return {
      'Booking ID': line.bookingID,
      Currency: data.hostByURL.paymentDetails?.currency,
      'Host Amount': hostAmount,
      'Statement Date': DateTime.fromISO(line.statementDate, {
        zone: 'utc',
      }).toLocaleString(DateTime.DATE_SHORT),
      'Transaction Type': line.type,
      'Trip Title': trip.title,
      'Trip End': DateTime.fromISO(line.groupEndDate, {
        zone: trip.endTimezone,
      }).toLocaleString(DateTime.DATE_SHORT),
      'Trip Start': DateTime.fromISO(line.groupStartDate, {
        zone: trip.startTimezone,
      }).toLocaleString(DateTime.DATE_SHORT),
      'User ID': line.userID,
    };
  });
}

export function StatementButton({
  month,
  hostURL,
}: {
  month: { startDate: DateTime; endDate: DateTime };
  hostURL: string;
}) {
  const [total, setTotal] = useState<{
    amountZD: number;
    currency: Currency;
  }>();

  const [getStatement, { loading }] = useLazyQuery(HostStatementDocument, {
    variables: {
      startDate: month.startDate.toISO(),
      endDate: month.endDate.toISO(),
      hostURL,
    },
  });

  // Trigger query asynchronously, so that it pre-loads and sets the total amount on the button once ready
  useEffect(() => {
    async function getTotal() {
      const res = await getStatement({
        fetchPolicy: 'cache-first',
      });

      // Do nothing if there is an error (we can try again on click)
      if (res.error) return;

      // Note we default to GBP if host payment details aren't set up yet.
      const currency =
        res.data.hostByURL.paymentDetails?.currency || Currency.GBP;
      const { lines } = res.data.hostByURL.statement;

      // Sum up the host amounts
      const sum = lines.reduce((runningSum: number, current) => {
        return runningSum + current.hostAmount.amountZD;
      }, 0);

      // Set the total
      setTotal({ currency, amountZD: sum });
    }
    getTotal();
  }, [getStatement]);

  const statementName = month.endDate.toLocaleString(DateTime.DATE_MED);

  if (loading && !total) {
    return (
      <Button>
        {statementName} (
        <Spinner animation="border" size="sm" className="ms-1 me-1" />)
      </Button>
    );
  }

  return (
    <Button
      onClick={async () => {
        const res = await getStatement({ fetchPolicy: 'network-only' });

        if (res.error) {
          alert('Sorry - the statement failed to download.');
        }

        const csvName = `${statementName.replace(' ', '_')}.csv`;

        // Create the CSV raw file (as a blob)
        const csvData = formatCSV(res.data);
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

        // Automatically download
        if ((window.navigator as any).msSaveOrOpenBlob)
          // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
          (window.navigator as any).msSaveBlob(blob, csvName);
        else {
          // Otherwise create an <a> element and click on it
          const a = window.document.createElement('a');
          a.href = window.URL.createObjectURL(blob);
          a.type = 'text/csv';
          a.download = csvName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }}
    >
      {statementName}{' '}
      {total && (
        <>
          (<PriceFormat amount={total.amountZD} currencyIso={total.currency} />)
        </>
      )}
    </Button>
  );
}

/**
 * Enable downloading host statements
 *
 * TMT monthly statements are on the last Sunday or Thursday of every month
 *
 */
export default function Statements({ hostURL }: { hostURL: string }) {
  const monthsList = useMemo(() => createMonthsList(), []);

  return (
    <>
      <h1>Transaction Statements</h1>

      <p>
        Transaction statements include all transactions (payments and refunds).
        The statement date for each payment will be the last day of that
        particular trip, as the payment is held in a trust fund up to that
        point.
      </p>

      <div className="d-grid gap-2">
        {monthsList.map((month) => {
          return (
            <StatementButton
              key={month.endDate.toLocaleString(DateTime.DATE_SHORT)}
              month={month}
              hostURL={hostURL}
            />
          );
        })}
      </div>
    </>
  );
}
